import React, { useState, useEffect, Component } from 'react';
import './LiveDemo.css';

/**
 * Ascona AI — Enterprise Spec-to-PR Control Plane
 * Interactive Customer Workbench & Simulation Environment
 */

// Error Boundary to guarantee no unhandled runtime error can ever cause a black screen
class WorkbenchErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error("Workbench ErrorBoundary caught:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="workbench-error-fallback">
          <div className="workbench-error-card">
            <h3>⚠️ Workbench Diagnostics &amp; Auto-Recovery</h3>
            <p>An unexpected interface state occurred: {this.state.error?.message || 'Render exception'}</p>
            <button
              type="button"
              className="workbench-btn-primary"
              onClick={() => {
                this.setState({ hasError: false, error: null });
                if (typeof window !== 'undefined') window.location.reload();
              }}
            >
              ↺ Reset Session
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const ENTERPRISE_SPECS = [
  {
    id: 'rfc-408',
    key: 'RFC-408',
    title: 'Enterprise Mid-Cycle Plan Upgrades with Financial Approval',
    author: 'Elena Rostova (Staff Platform Architect)',
    targetSprint: 'Sprint 24',
    status: 'In-Review',
    services: ['billing-core', 'auth-service', 'subscription-api', 'ledger-db'],
    hazardDetected: true,
    hazardText: "Missing OAuth token scope 'billing:finance_override' across auth-service and subscription-api.",
    hazardImpact: "Downstream validator throws HTTP 403 Forbidden on all enterprise tier upgrades before reaching ledger.",
    brief: `# RFC-408: Enterprise Mid-Cycle Plan Upgrades with Financial Controller Approval

## Executive Summary
Enable enterprise tier customers on annual contracts to execute mid-cycle seat & tier upgrades immediately without invoice forfeiture, provided financial controller approval token is validated.

## Core Architectural Constraints
1. Multi-tenant atomic balance calculations down to the second.
2. Must verify caller possesses 'billing:finance_override' OAuth scope issued by auth-service.
3. Two-phase commit in ledger-db with financial_approval_id recorded.
4. Deterministic Gherkin test runners to block unapproved plan mutations at CI.`
  },
  {
    id: 'rfc-312',
    key: 'RFC-312',
    title: 'High-Throughput Stripe Usage Metering & Redis Bloom Filter',
    author: 'Marcus Vance (VP of Engineering)',
    targetSprint: 'Sprint 25',
    status: 'Approved',
    services: ['usage-metering', 'invoicing-worker', 'stripe-sync-job'],
    hazardDetected: false,
    hazardText: "Zero cross-service contract hazards detected across 14 indexed microservices.",
    hazardImpact: "All API contracts and protobuf definitions align with upstream gateway.",
    brief: `# RFC-312: High-Throughput Stripe Usage Metering Pipeline

## Executive Summary
Deduplicate 50,000+ API usage hits per hour using sliding-window Redis bloom filters, batch-aggregating records into hourly Stripe Metered API payloads.

## Core Architectural Constraints
1. 100% idempotent ingestion using X-Idempotency-Key.
2. Sliding-window memory buffer to avoid exhausting Stripe API rate limits.
3. Automated dead-letter queue (DLQ) in Kafka on failed webhooks.`
  },
  {
    id: 'rfc-204',
    key: 'RFC-204',
    title: 'Multi-Tenant OAuth2 PKCE & Ephemeral Session Invalidation',
    author: 'David Chen (Principal Security Lead)',
    targetSprint: 'Sprint 26',
    status: 'Draft',
    services: ['gateway-service', 'auth-service', 'audit-logger'],
    hazardDetected: false,
    hazardText: "Zero cross-service contract hazards detected across 14 indexed microservices.",
    hazardImpact: "Cross-repo ASTs verify PKCE challenge/verifier schemas match RFC 7636.",
    brief: `# RFC-204: Multi-Tenant OAuth2 PKCE & Ephemeral Session Invalidation

## Executive Summary
Enforce PKCE authorization code grants with replay attack mitigation for client-side single page applications (SPAs) and Cursor IDE agents.

## Core Architectural Constraints
1. SHA-256 code challenge computation on initial auth prompt.
2. Immediate session revocation broadcast to Redis cluster.
3. Zero Data Retention (ZDR) ephemeral memory parsing.`
  }
];

const REPOS_LIST = [
  {
    name: "billing-core",
    role: "Primary Service",
    lang: "TypeScript",
    branch: "main",
    astNodes: 1420,
    files: 48,
    hazard: false,
    filePath: "repos/billing-core/src/BillingUpgradeHandler.ts",
    codeSnippet: `// repos/billing-core/src/BillingUpgradeHandler.ts
import { Request, Response } from 'express';
import { subscriptionApi } from '@acme/subscription-api';
import { billingEngine } from './billingEngine';
import { verifyOAuthScope } from '@acme/auth-service';

export async function handleMidCycleUpgrade(req: Request, res: Response) {
  const { tenantId, newTierId } = req.body;
  const quote = await subscriptionApi.calcProration(tenantId, newTierId);

  // Contract Gate Checkpoint:
  // Requires 'billing:finance_override' scope before ledger commit.
  const authHeader = req.headers['authorization'];
  const token = await verifyOAuthScope(authHeader, 'billing:finance_override');
  if (!token.hasApproval) {
    throw new Error('HTTP 403: Financial Controller override token required.');
  }

  return await billingEngine.applyUpgrade(tenantId, quote, token.approvalId);
}`
  },
  {
    name: "auth-service",
    role: "OAuth & Token Scopes",
    lang: "Go",
    branch: "main",
    astNodes: 2840,
    files: 82,
    hazard: true,
    filePath: "repos/auth-service/scopes/definitions.go",
    codeSnippet: `// repos/auth-service/scopes/definitions.go
package scopes

// Certified OAuth2 Scopes for Acme Microservices
const (
  ScopeTenantRead        = "tenant:read"
  ScopeTenantAdmin       = "tenant:admin"
  ScopeBillingRead       = "billing:read"
  ScopeBillingWrite      = "billing:write"
  // [Ascona Injection]: Required for RFC-408 mid-cycle upgrades
  ScopeBillingFinanceOverride = "billing:finance_override"
)`
  },
  {
    name: "subscription-api",
    role: "Tier Upgrade Engine",
    lang: "TypeScript",
    branch: "main",
    astNodes: 1980,
    files: 64,
    hazard: true,
    filePath: "repos/subscription-api/src/ProrationEngine.ts",
    codeSnippet: `// repos/subscription-api/src/ProrationEngine.ts
export class ProrationEngine {
  public async calcProration(tenantId: string, newTierId: string) {
    const activeSub = await this.db.getActiveSubscription(tenantId);
    const remainingSeconds = activeSub.expiresAt - Date.now();
    const deltaAmount = this.computeDelta(activeSub.tier, newTierId, remainingSeconds);
    return {
      deltaAmount,
      currency: 'USD',
      calculatedAt: new Date().toISOString()
    };
  }
}`
  },
  {
    name: "invoicing-worker",
    role: "Async Queue Consumer",
    lang: "Rust",
    branch: "main",
    astNodes: 950,
    files: 24,
    hazard: false,
    filePath: "repos/invoicing-worker/src/worker.rs",
    codeSnippet: `// repos/invoicing-worker/src/worker.rs
pub async fn process_invoice_event(event: InvoiceEvent) -> Result<(), WorkerError> {
    info!("Processing mid-cycle invoice for tenant: {}", event.tenant_id);
    let invoice = generate_pdf(&event).await?;
    dispatch_to_stripe(&invoice).await?;
    Ok(())
}`
  },
  {
    name: "ledger-db",
    role: "Double-Entry Ledger",
    lang: "PostgreSQL",
    branch: "main",
    astNodes: 430,
    files: 18,
    hazard: false,
    filePath: "repos/ledger-db/migrations/20261017_audit_ledger.sql",
    codeSnippet: `-- repos/ledger-db/migrations/20261017_audit_ledger.sql
CREATE TABLE IF NOT EXISTS ledger_audit_transactions (
    id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
    tenant_id VARCHAR(64) NOT NULL,
    financial_approval_id VARCHAR(128) NOT NULL,
    delta_amount_cents BIGINT NOT NULL,
    applied_at TIMESTAMP WITH TIME ZONE DEFAULT NOW()
);`
  },
  {
    name: "gateway-service",
    role: "Envoy Edge Gateway",
    lang: "Go",
    branch: "main",
    astNodes: 3120,
    files: 92,
    hazard: false,
    filePath: "repos/gateway-service/routes.yaml",
    codeSnippet: `# repos/gateway-service/routes.yaml
routes:
  - match: { prefix: "/v1/billing/upgrade" }
    route: { cluster: "billing-core" }
    cors: { allow_origin: ["https://app.acme.com"] }`
  },
  {
    name: "checkout-web",
    role: "Self-Service Portal",
    lang: "React / Next.js",
    branch: "main",
    astNodes: 4200,
    files: 120,
    hazard: false,
    filePath: "repos/checkout-web/pages/upgrade.tsx",
    codeSnippet: `// repos/checkout-web/pages/upgrade.tsx
export default function UpgradeModal({ currentTier, onConfirmUpgrade }) {
  return (
    <div className="upgrade-dialog">
      <h3>Confirm Mid-Cycle Tier Upgrade</h3>
      <p>Your team will be provisioned immediately upon controller authorization.</p>
    </div>
  );
}`
  },
  {
    name: "stripe-sync-job",
    role: "Webhook Reconciler",
    lang: "Python",
    branch: "main",
    astNodes: 820,
    files: 22,
    hazard: false,
    filePath: "repos/stripe-sync-job/reconcile.py",
    codeSnippet: `# repos/stripe-sync-job/reconcile.py
def sync_proration_invoice(event_payload):
    stripe.InvoiceItem.create(
        customer=event_payload['stripe_cust_id'],
        amount=event_payload['delta_cents'],
        currency='usd',
        description='Mid-cycle upgrade proration'
    )`
  },
  {
    name: "proration-calc",
    role: "Delta Math Engine",
    lang: "TypeScript",
    branch: "main",
    astNodes: 640,
    files: 16,
    hazard: false,
    filePath: "repos/proration-calc/src/math.ts",
    codeSnippet: `export function computeDeltaCents(oldRate: number, newRate: number, seconds: number) {
  const perSecond = (newRate - oldRate) / (365 * 86400);
  return Math.round(perSecond * seconds);
}`
  },
  {
    name: "audit-logger",
    role: "SOC 2 Audit Stream",
    lang: "Go",
    branch: "main",
    astNodes: 510,
    files: 14,
    hazard: false,
    filePath: "repos/audit-logger/main.go",
    codeSnippet: `func LogSecurityEvent(event AuditEvent) {
  slog.Info("Audit Log Recorded", "tenant", event.Tenant, "approval_id", event.ApprovalID)
}`
  },
  {
    name: "dunning-engine",
    role: "Card Recovery Schedule",
    lang: "TypeScript",
    branch: "main",
    astNodes: 1120,
    files: 34,
    hazard: false,
    filePath: "repos/dunning-engine/src/dunning.ts",
    codeSnippet: `export async function scheduleDunningRetry(subId: string) {
  return await queue.enqueue('retry_charge', { subId, delay: '48h' });
}`
  },
  {
    name: "tax-service",
    role: "Avalara Tax Adapter",
    lang: "TypeScript",
    branch: "main",
    astNodes: 780,
    files: 26,
    hazard: false,
    filePath: "repos/tax-service/src/tax.ts",
    codeSnippet: `export async function calculateTax(amount: number, zipCode: string) {
  return await avalara.resolveTaxRate(zipCode) * amount;
}`
  },
  {
    name: "usage-metering",
    role: "Redis Buffer Engine",
    lang: "Rust",
    branch: "main",
    astNodes: 1340,
    files: 38,
    hazard: false,
    filePath: "repos/usage-metering/src/bloom.rs",
    codeSnippet: `pub fn check_idempotency(key: &str) -> bool {
  // Bloom filter sliding window check
  REDIS_CLIENT.check_and_set(key)
}`
  },
  {
    name: "notification-bus",
    role: "Kafka Event Bus",
    lang: "Go",
    branch: "main",
    astNodes: 1860,
    files: 52,
    hazard: false,
    filePath: "repos/notification-bus/producer.go",
    codeSnippet: `func PublishUpgradeEvent(evt UpgradeEvent) error {
  return kafkaProducer.SendMessage("billing.upgrades.v1", evt)
}`
  }
];

const INITIAL_JIRA_ISSUES = [
  {
    key: "BILL-501",
    type: "Spike",
    title: "Inject missing 'billing:finance_override' OAuth token scope into auth-service",
    points: 3,
    status: "Sprint Ready",
    assignee: "Platform Lead",
    service: "auth-service",
    story: "As a security architect, I want auth-service to issue certified financial controller tokens, so that high-value mid-cycle tier mutations can proceed safely without runtime 403s.",
    gherkin: `Scenario: Issue finance override OAuth token
  Given a certified Financial Controller requesting mid-cycle override
  When authenticating via auth-service/v2/oauth/token
  Then grant JWT containing scope 'billing:finance_override'
  And enforce 15-minute ephemeral expiration`
  },
  {
    key: "BILL-502",
    type: "Story",
    title: "Enterprise mid-cycle upgrade mutation with strict finance approval guard",
    points: 5,
    status: "Ready for Dev",
    assignee: "Billing Senior Eng",
    service: "billing-core",
    story: "As an enterprise customer, I want to upgrade my team's plan mid-billing cycle with controller verification, so that our engineers are provisioned immediately without manual invoicing bottlenecks.",
    gherkin: `Scenario: Mid-cycle enterprise upgrade with controller validation
  Given an active enterprise tenant on Annual Starter plan
  When tenant requests mid-cycle tier upgrade with financial token
  Then verify caller possesses 'billing:finance_override' scope
  And record financial_approval_id in ledger-db audit transaction`
  },
  {
    key: "BILL-503",
    type: "Story",
    title: "Second-by-second proration calculator & atomic ledger locking",
    points: 8,
    status: "Ready for Dev",
    assignee: "Fintech Backend Lead",
    service: "subscription-api",
    story: "As a billing operator, I want second-by-second credit proration calculated atomically, so that enterprise invoices reflect exact usage without reconciliation disputes.",
    gherkin: `Scenario: Atomic proration delta calculation
  Given an existing subscription with 42 days remaining
  When mid-cycle upgrade is confirmed
  Then compute second-by-second credit delta against Stripe invoice
  And commit atomic two-phase commit to ledger-db`
  },
  {
    key: "BILL-504",
    type: "Story",
    title: "Deterministic Given-When-Then BDD contract gate suite for CI",
    points: 5,
    status: "QA Ready",
    assignee: "QA Lead",
    service: "ascona-action",
    story: "As a VP of Engineering, I want GitHub Actions to block any PR mutating billing handlers without the financial approval guard, so that breaking contract bugs never hit production.",
    gherkin: `Scenario: CI PR contract compliance verification
  Given a pull request mutating BillingUpgradeHandler.ts
  When ascona-action inspects AST diff
  Then verify presence of verifyFinanceApproval() security guard
  And block PR merge if guard is bypassed or missing`
  }
];

export default function LiveDemo({ onNavigate }) {
  return (
    <WorkbenchErrorBoundary>
      <LiveDemoInner onNavigate={onNavigate} />
    </WorkbenchErrorBoundary>
  );
}

function LiveDemoInner({ onNavigate }) {
  // Navigation tabs: 'ast-spec' | 'ci-gate' | 'backlog' | 'telemetry'
  const [activeTab, setActiveTab] = useState('ast-spec');

  // Spec selection
  const [selectedSpecId, setSelectedSpecId] = useState('rfc-408');
  const activeSpec = ENTERPRISE_SPECS.find(s => s.id === selectedSpecId) || ENTERPRISE_SPECS[0];

  // Selected repository in explorer
  const [selectedRepoName, setSelectedRepoName] = useState('billing-core');
  const activeRepo = REPOS_LIST.find(r => r.name === selectedRepoName) || REPOS_LIST[0];

  // Active IDE code view: 'spec' | 'code'
  const [activeIdeView, setActiveIdeView] = useState('spec');

  // Hazard State (Toggleable)
  const [hazardResolved, setHazardResolved] = useState(false);

  // Terminal Execution State
  const [isCliRunning, setIsCliRunning] = useState(false);
  const [terminalLogs, setTerminalLogs] = useState([
    "$ npx @ascona/cli parse specs/rfc-408.md --multi-repo --sync-jira",
    "[AST] Indexed 14 microservice repositories (21,870 AST nodes parsed) in 0.38s",
    "[EVAL] Querying cross-tenant evaluator against 14,800+ historical diff/revert pairs...",
    "[HAZARD] ⚠️ Missing OAuth scope 'billing:finance_override' across auth-service & subscription-api",
    "[JIRA] Auto-generated 4 issues with Given-When-Then criteria (BILL-501..504)"
  ]);

  // Pull Request State
  const [isPrPatched, setIsPrPatched] = useState(false);
  const [isEvaluatingCi, setIsEvaluatingCi] = useState(false);
  const [prMerged, setPrMerged] = useState(false);
  const [showMergeConfirm, setShowMergeConfirm] = useState(false);
  const [activePrSubTab, setActivePrSubTab] = useState('diff'); // 'conversation' | 'commits' | 'checks' | 'diff' | 'gherkin'
  const [expandedCheckId, setExpandedCheckId] = useState('gate'); // which check is expanded

  // PR User comments
  const [userCommentText, setUserCommentText] = useState('');
  const [customComments, setCustomComments] = useState([]);

  // Backlog state
  const [activeBacklogTool, setActiveBacklogTool] = useState('jira'); // 'jira' | 'linear'
  const [backlogFilter, setBacklogFilter] = useState('all'); // 'all' | 'Sprint Ready' | 'Ready for Dev' | 'QA Ready' | 'Done'
  const [jiraIssues, setJiraIssues] = useState(INITIAL_JIRA_ISSUES);
  const [selectedIssueKey, setSelectedIssueKey] = useState('BILL-502');
  const [isSyncingBacklog, setIsSyncingBacklog] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState('Just now (REST v3 · 200 OK)');

  // Telemetry interactive calculator
  const [teamSizeInput, setTeamSizeInput] = useState(45);
  const [avgSalaryInput, setAvgSalaryInput] = useState(165000);

  // Notification Toast message
  const [toastMessage, setToastMessage] = useState('⚡ Ascona Control Plane v3.1 live across 14 connected microservices.');

  // Helper to trigger toast
  const showToast = (msg) => {
    setToastMessage(msg);
  };

  // Action: Run CLI Parser
  const handleRunCli = () => {
    setIsCliRunning(true);
    showToast("⚡ Running @ascona/cli multi-repo AST indexer across 14 repositories...");
    setTerminalLogs(prev => [
      ...prev,
      `$ npx @ascona/cli parse specs/${activeSpec.id}.md --multi-repo --verbose`,
      `[AST] Analyzing cross-repo contract boundaries for ${activeSpec.key}...`,
      `[AST] Re-indexing 14 connected repositories... [0.42s]`
    ]);

    setTimeout(() => {
      setIsCliRunning(false);
      setTerminalLogs(prev => [
        ...prev,
        `[AST] Completed. Evaluated ${REPOS_LIST.reduce((a, b) => a + b.astNodes, 0).toLocaleString()} nodes.`,
        hazardResolved ? "[EVAL] ✓ 0 breaking contract hazards detected." : `[HAZARD] ⚠️ ${activeSpec.hazardText}`,
        `[SYNC] Jira REST v3 & Linear sync ready (4 stories verified).`
      ]);
      showToast(`✔ @ascona/cli completed successfully in 0.42s.`);
    }, 850);
  };

  // Action: Clear Terminal
  const handleClearTerminal = () => {
    setTerminalLogs([`$ npx @ascona/cli ready on 14 repositories.`]);
    showToast("Terminal buffer cleared.");
  };

  // Action: Remediate / Toggle Hazard
  const handleToggleHazard = () => {
    if (!hazardResolved) {
      setHazardResolved(true);
      showToast("✔ Contract Hazard Remediated: Prerequisite Spike (BILL-501) injected into auth-service.");
      setTerminalLogs(prev => [
        ...prev,
        "[REMEDIATION] Injected 'billing:finance_override' scope into auth-service/scopes.config.ts",
        "[AST] Re-evaluated cross-service graph: ALL 14 SERVICES CONTRACTS SATISFIED."
      ]);
    } else {
      setHazardResolved(false);
      showToast("⚠️ Contract Hazard re-introduced: Missing OAuth token scope active.");
    }
  };

  // Action: Toggle PR Contract Guard Patch
  const handleTogglePrPatch = () => {
    setIsEvaluatingCi(true);
    if (!isPrPatched) {
      showToast("⚡ Applying autonomous contract guard patch to BillingUpgradeHandler.ts...");
      setTimeout(() => {
        setIsPrPatched(true);
        setIsEvaluatingCi(false);
        showToast("✔ GitHub Actions ascona/spec-contract-gate PASSED (100% Contract Compliance).");
      }, 700);
    } else {
      showToast("⚠️ Reverting contract guard patch to test CI gate failure...");
      setTimeout(() => {
        setIsPrPatched(false);
        setIsEvaluatingCi(false);
        setPrMerged(false);
        setShowMergeConfirm(false);
        showToast("❌ ascona/spec-contract-gate BLOCKED: Missing financial approval guard.");
      }, 600);
    }
  };

  // Action: Merge Pull Request
  const handleMergePr = () => {
    if (!isPrPatched) return;
    setPrMerged(true);
    setShowMergeConfirm(false);
    // Update linked Jira issue status to Done
    setJiraIssues(prev => prev.map(issue => {
      if (issue.key === 'BILL-502') return { ...issue, status: 'Done' };
      return issue;
    }));
    showToast("🎉 Pull Request #342 successfully merged into main! Deployed to staging and Jira BILL-502 marked Done.");
  };

  // Action: Reset PR State
  const handleResetPrState = () => {
    setPrMerged(false);
    setIsPrPatched(false);
    setShowMergeConfirm(false);
    showToast("↺ PR #342 reset to initial unpatched state.");
  };

  // Action: Add Comment to PR
  const handleAddComment = (e) => {
    e.preventDefault();
    if (!userCommentText.trim()) return;
    const newComment = {
      id: Date.now(),
      author: "You (Reviewer)",
      avatar: "👤",
      role: "Engineering Reviewer",
      time: "Just now",
      content: userCommentText.trim()
    };
    setCustomComments(prev => [...prev, newComment]);
    setUserCommentText('');
    showToast("💬 Comment posted to PR #342.");
  };

  // Action: Sync Backlog to Jira
  const handleSyncBacklog = () => {
    setIsSyncingBacklog(true);
    showToast(`⚡ Syncing 4 verified stories with Jira Cloud (REST v3 & Forge v3.1)...`);
    setTimeout(() => {
      setIsSyncingBacklog(false);
      const timeStr = new Date().toLocaleTimeString();
      setLastSyncTime(`${timeStr} (REST v3 · 200 OK)`);
      showToast(`✔ 2-Way Sync Complete! 4 tickets updated in Jira & Linear at ${timeStr}.`);
    }, 700);
  };

  // Action: Change Issue Status
  const handleUpdateIssueStatus = (key, nextStatus) => {
    setJiraIssues(prev => prev.map(issue => {
      if (issue.key === key) return { ...issue, status: nextStatus };
      return issue;
    }));
    showToast(`Updated ${key} status to "${nextStatus}".`);
  };

  // Action: Adjust Issue Points
  const handleAdjustPoints = (key, delta) => {
    setJiraIssues(prev => prev.map(issue => {
      if (issue.key === key) {
        const newPts = Math.max(1, (issue.points || 0) + delta);
        return { ...issue, points: newPts };
      }
      return issue;
    }));
    showToast(`Calibrated story points for ${key}.`);
  };

  // Action: Reset Workbench
  const handleResetAll = () => {
    setHazardResolved(false);
    setIsPrPatched(false);
    setPrMerged(false);
    setShowMergeConfirm(false);
    setSelectedSpecId('rfc-408');
    setSelectedRepoName('billing-core');
    setSelectedIssueKey('BILL-502');
    setActiveIdeView('spec');
    setActivePrSubTab('diff');
    setCustomComments([]);
    setJiraIssues(INITIAL_JIRA_ISSUES);
    setTerminalLogs([
      "$ npx @ascona/cli parse specs/rfc-408.md --multi-repo --sync-jira",
      "[AST] Indexed 14 microservice repositories (21,870 AST nodes parsed) in 0.38s",
      "[EVAL] Querying cross-tenant evaluator against 14,800+ historical diff/revert pairs...",
      "[HAZARD] ⚠️ Missing OAuth scope 'billing:finance_override' across auth-service & subscription-api",
      "[JIRA] Auto-generated 4 issues with Given-When-Then criteria (BILL-501..504)"
    ]);
    showToast("↺ Workbench reset to default production baseline.");
  };

  const activeIssue = jiraIssues.find(i => i.key === selectedIssueKey) || jiraIssues[1];

  const filteredIssues = backlogFilter === 'all'
    ? jiraIssues
    : jiraIssues.filter(i => i.status === backlogFilter);

  // Dynamic calculations for Telemetry tab
  const hoursSavedPerYear = Math.round(teamSizeInput * 2.5 * 48); // 2.5 hrs/wk * 48 wks
  const hourlyRate = Math.round(avgSalaryInput / 2000);
  const annualSavingsDollars = Math.round(hoursSavedPerYear * hourlyRate);
  const monthlyReworkSavings = Math.round((teamSizeInput / 45) * 14200);

  return (
    <div className="live-demo-container">
      <div className="live-demo-bg-pattern"></div>

      {/* Sticky Topbar */}
      <header className="workbench-header">
        <div className="workbench-nav-row">
          <div
            className="workbench-brand"
            onClick={() => onNavigate('/')}
            role="button"
            tabIndex={0}
            title="Return to Ascona AI Home"
          >
            <div className="workbench-brand-logo">A</div>
            <div>
              <span className="workbench-brand-title">Ascona AI</span>
              <span style={{ fontSize: '0.78rem', color: '#94a3b8', marginLeft: '6px' }}>Control Plane v3.1</span>
            </div>
          </div>

          <div className="workbench-org-badge">
            <span>Tenant:</span>
            <strong>Acme Enterprise</strong>
            <span style={{ color: '#484f58' }}>|</span>
            <span>14 Connected Repositories</span>
          </div>

          {/* Status Pills */}
          <div className="workbench-status-pills">
            <span
              className="workbench-status-pill online"
              onClick={() => showToast("Jira Cloud REST v3 connection healthy. Bidirectional sync active.")}
              title="Click to check status"
            >
              🔷 Jira REST v3: Synced
            </span>
            <span
              className="workbench-status-pill online"
              onClick={() => showToast("GitHub Actions @ascona/action active across all 14 repositories.")}
              title="Click to check status"
            >
              ⚙️ GitHub Actions: Active
            </span>
            <span
              className={`workbench-status-pill ${hazardResolved ? 'online' : 'warning'}`}
              onClick={handleToggleHazard}
              title="Click to toggle hazard state"
            >
              {hazardResolved ? '✓ 0 Schema Hazards' : '⚠️ 1 Contract Hazard Detected'}
            </span>
          </div>

          <div className="workbench-nav-actions">
            <button
              type="button"
              className="workbench-btn-secondary"
              onClick={handleResetAll}
              title="Reset all states"
            >
              ↺ Reset
            </button>
            <button
              type="button"
              className="workbench-btn-secondary"
              onClick={() => onNavigate('/')}
            >
              ← Overview
            </button>
            <button
              type="button"
              className="workbench-btn-primary"
              onClick={() => {
                onNavigate('/');
                setTimeout(() => {
                  const el = document.getElementById('signup');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }, 100);
              }}
            >
              Request Enterprise Trial →
            </button>
          </div>
        </div>
      </header>

      {/* Subnav & View Tabs */}
      <div className="workbench-subnav">
        <div className="workbench-subnav-inner">
          <div className="workbench-tabs">
            <button
              type="button"
              className={`workbench-tab-btn ${activeTab === 'ast-spec' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('ast-spec');
                showToast("Opened Multi-Repo AST Context Graph & Workspace.");
              }}
            >
              🏛️ Spec &amp; Multi-Repo AST Graph
            </button>
            <button
              type="button"
              className={`workbench-tab-btn ${activeTab === 'ci-gate' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('ci-gate');
                showToast("Opened GitHub Actions CI Merge Gate & PR Inspector.");
              }}
            >
              🛡️ GitHub Actions CI Merge Gate {isPrPatched ? <span style={{ color: '#34d399' }}>(Ready)</span> : <span style={{ color: '#f87171' }}>(Blocked)</span>}
            </button>
            <button
              type="button"
              className={`workbench-tab-btn ${activeTab === 'backlog' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('backlog');
                showToast("Opened Jira Cloud & Linear Backlog View.");
              }}
            >
              🔷 Jira &amp; Linear Backlog (4 Issues)
            </button>
            <button
              type="button"
              className={`workbench-tab-btn ${activeTab === 'telemetry' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('telemetry');
                showToast("Opened Production Telemetry & ROI Calculator.");
              }}
            >
              📊 Production Benchmarks &amp; ROI
            </button>
          </div>

          {/* Active Spec Selector */}
          <div className="workbench-spec-picker">
            <span className="workbench-spec-label">Active Spec:</span>
            <select
              className="workbench-spec-select"
              value={selectedSpecId}
              onChange={(e) => {
                setSelectedSpecId(e.target.value);
                showToast(`Loaded ${e.target.value.toUpperCase()} specification brief.`);
              }}
            >
              {ENTERPRISE_SPECS.map(spec => (
                <option key={spec.id} value={spec.id}>
                  {spec.key}: {spec.title.slice(0, 38)}...
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Live Toast Banner */}
      <div className="workbench-toast-bar">
        <div>
          <span>Status Notification:</span> <strong>{toastMessage}</strong>
        </div>
        <div style={{ fontSize: '0.75rem', opacity: 0.85 }}>
          Interactive Control Plane: Click any tab, repository node, patch button, or ticket to inspect live state.
        </div>
      </div>

      {/* Main Workspace Canvas */}
      <main className="workbench-canvas">
        {/* TAB 1: Spec & Multi-Repo AST Engine */}
        {activeTab === 'ast-spec' && (
          <div className="workbench-grid-2col">
            {/* Left Card: Multi-Repo Workspace & Editor */}
            <div className="workbench-card">
              <div className="workbench-card-header">
                <div className="workbench-card-title">
                  <span>⚡ Cursor / VS Code Workspace</span>
                  <span style={{ color: '#64748b' }}>·</span>
                  <span style={{ color: '#a5b4fc', fontSize: '0.8rem' }}>14 Connected Repos</span>
                </div>
                <div className="workbench-card-actions">
                  <button
                    type="button"
                    className="workbench-sm-btn"
                    onClick={handleRunCli}
                    disabled={isCliRunning}
                  >
                    {isCliRunning ? 'Running CLI...' : '▶ Run @ascona/cli parse'}
                  </button>
                  <button
                    type="button"
                    className="workbench-sm-btn"
                    onClick={handleClearTerminal}
                  >
                    Clear
                  </button>
                </div>
              </div>

              <div className="workbench-ide-body">
                {/* 14 Repositories Explorer */}
                <div className="workbench-ide-sidebar">
                  <div className="workbench-sidebar-head">Connected Repos (14)</div>
                  <ul className="workbench-repo-list">
                    {REPOS_LIST.map(repo => {
                      const isAffected = activeSpec.services.includes(repo.name);
                      return (
                        <li
                          key={repo.name}
                          className={`workbench-repo-item ${selectedRepoName === repo.name ? 'active' : ''} ${repo.hazard && !hazardResolved ? 'has-hazard' : ''}`}
                          onClick={() => {
                            setSelectedRepoName(repo.name);
                            setActiveIdeView('code');
                            showToast(`Inspecting source for ${repo.name} (${repo.astNodes} AST nodes).`);
                          }}
                        >
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {repo.hazard && !hazardResolved ? '⚠️' : '📦'} {repo.name}
                          </span>
                          {isAffected && (
                            <span style={{ fontSize: '0.68rem', color: '#38bdf8', fontWeight: 700 }}>
                              SPEC
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>

                {/* Editor & Spec Viewer */}
                <div className="workbench-ide-editor">
                  <div className="workbench-tab-row">
                    <button
                      type="button"
                      className={`workbench-tab-chip ${activeIdeView === 'spec' ? 'active' : ''}`}
                      onClick={() => setActiveIdeView('spec')}
                    >
                      <span>📄</span> {activeSpec.key}.md
                    </button>
                    <button
                      type="button"
                      className={`workbench-tab-chip ${activeIdeView === 'code' ? 'active' : ''}`}
                      onClick={() => setActiveIdeView('code')}
                    >
                      <span>💻</span> {activeRepo.name} source
                    </button>
                    <div style={{ marginLeft: 'auto', paddingRight: '12px', fontSize: '0.72rem', color: '#8b949e', display: 'flex', alignItems: 'center' }}>
                      {activeIdeView === 'spec' ? `${activeSpec.targetSprint}` : `${activeRepo.lang} · ${activeRepo.astNodes} nodes`}
                    </div>
                  </div>

                  <div className="workbench-code-content">
                    {activeIdeView === 'spec' ? (
                      <>
                        <div style={{ background: '#161b22', padding: '10px 12px', borderRadius: '6px', marginBottom: '12px', border: '1px solid #30363d' }}>
                          <div style={{ fontWeight: 700, color: '#38bdf8', marginBottom: '4px' }}>
                            {activeSpec.key}: {activeSpec.title}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                            Author: <strong style={{ color: '#fff' }}>{activeSpec.author}</strong> · Status: <span style={{ color: '#34d399', fontWeight: 600 }}>{activeSpec.status}</span> · Bound Services: {activeSpec.services.join(', ')}
                          </div>
                        </div>

                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.8rem', color: '#cbd5e1' }}>
                          {activeSpec.brief}
                        </pre>
                      </>
                    ) : (
                      <>
                        <div style={{ background: '#161b22', padding: '8px 12px', borderRadius: '6px', marginBottom: '10px', border: '1px solid #30363d', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ fontSize: '0.78rem', color: '#38bdf8', fontFamily: 'monospace' }}>
                            {activeRepo.filePath}
                          </div>
                          <div style={{ fontSize: '0.72rem', color: '#8b949e' }}>
                            Branch: <strong style={{ color: '#fff' }}>{activeRepo.branch}</strong>
                          </div>
                        </div>

                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.8rem', color: '#cbd5e1', background: '#090d16', padding: '12px', borderRadius: '6px' }}>
                          {activeRepo.codeSnippet}
                        </pre>
                      </>
                    )}

                    {/* Inspection of Selected Repo */}
                    <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #30363d' }}>
                      <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#a5b4fc', textTransform: 'uppercase', marginBottom: '6px' }}>
                        Selected Repository: {activeRepo.name}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', fontSize: '0.75rem' }}>
                        <div style={{ background: '#161b22', padding: '6px 8px', borderRadius: '4px' }}>
                          <span style={{ color: '#8b949e' }}>Language:</span> <strong>{activeRepo.lang}</strong>
                        </div>
                        <div style={{ background: '#161b22', padding: '6px 8px', borderRadius: '4px' }}>
                          <span style={{ color: '#8b949e' }}>AST Nodes:</span> <strong>{activeRepo.astNodes}</strong>
                        </div>
                        <div style={{ background: '#161b22', padding: '6px 8px', borderRadius: '4px' }}>
                          <span style={{ color: '#8b949e' }}>Contract Status:</span>{' '}
                          <strong style={{ color: activeRepo.hazard && !hazardResolved ? '#f87171' : '#34d399' }}>
                            {activeRepo.hazard && !hazardResolved ? '⚠️ Scope Hazard' : '✓ Valid'}
                          </strong>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Terminal Logs */}
              <div className="workbench-terminal">
                {terminalLogs.map((log, idx) => (
                  <div
                    key={idx}
                    className={
                      log.startsWith('$') ? 'term-line-cmd' :
                      log.includes('HAZARD') ? 'term-line-error' :
                      log.includes('REMEDIATION') || log.includes('Completed') || log.includes('✓') ? 'term-line-success' :
                      log.includes('WARN') ? 'term-line-warn' : 'term-line-info'
                    }
                  >
                    {log}
                  </div>
                ))}
              </div>
            </div>

            {/* Right Card: Multi-Repo AST Context Graph & Cross-Tenant Evaluator */}
            <div className="workbench-card workbench-ast-pane">
              <div className="workbench-card-header">
                <div className="workbench-card-title">
                  <span>🏛️ Multi-Repo AST Graph &amp; Contract Engine</span>
                </div>
                <div className="workbench-card-actions">
                  <button
                    type="button"
                    className="workbench-sm-btn"
                    onClick={handleToggleHazard}
                    style={{ borderColor: hazardResolved ? '#10b981' : '#ef4444' }}
                  >
                    {hazardResolved ? '✓ Hazard Resolved' : '⚡ Auto-Remediate Hazard'}
                  </button>
                </div>
              </div>

              <div className="workbench-ast-body">
                {/* Hazard Notification Banner */}
                <div className={`workbench-hazard-alert ${hazardResolved ? 'resolved' : ''}`}>
                  <div className="workbench-hazard-icon">
                    {hazardResolved ? '🛡️' : '⚠️'}
                  </div>
                  <div className="workbench-hazard-content">
                    <h4>
                      {hazardResolved
                        ? 'Contract Re-evaluated: All Multi-Repo AST Dependencies Satisfied'
                        : 'Cross-Tenant Evaluator Alert: Missing OAuth Scope Hazard'}
                    </h4>
                    <p>
                      {hazardResolved
                        ? "Prerequisite Spike ticket (BILL-501) injected into auth-service. Token scope 'billing:finance_override' will be in place before billing-core implementation begins."
                        : activeSpec.hazardText}
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.72rem', background: hazardResolved ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)', padding: '2px 6px', borderRadius: '4px', fontWeight: 700, color: hazardResolved ? '#34d399' : '#f87171' }}>
                        {hazardResolved ? 'Pre-Code Remediated' : 'Pre-Code Drift Detected'}
                      </span>
                      <button
                        type="button"
                        className="workbench-sm-btn"
                        onClick={handleToggleHazard}
                        style={{ fontSize: '0.72rem', padding: '2px 8px' }}
                      >
                        {hazardResolved ? '↺ Re-introduce Hazard' : '⚡ Injected Prerequisite Spike (BILL-501)'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Microservice Topology Nodes (Interactive) */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>
                      Microservice Call Graph &amp; Contract Verification (Click any to inspect):
                    </span>
                    <span style={{ fontSize: '0.72rem', color: '#38bdf8' }}>14 of 14 Repos Connected</span>
                  </div>

                  <div className="workbench-topology-grid">
                    {REPOS_LIST.map(repo => {
                      const isHazardous = repo.hazard && !hazardResolved;
                      return (
                        <div
                          key={repo.name}
                          className={`workbench-topology-node ${selectedRepoName === repo.name ? 'active' : ''} ${isHazardous ? 'hazard' : ''} ${repo.hazard && hazardResolved ? 'resolved' : ''}`}
                          onClick={() => {
                            setSelectedRepoName(repo.name);
                            setActiveIdeView('code');
                            showToast(`Inspecting ${repo.name} contract status.`);
                          }}
                        >
                          <div className="node-title-row">
                            <span>{repo.name}</span>
                            <span className="node-lang-tag">{repo.lang}</span>
                          </div>
                          <div className="node-meta-row">
                            <span>{repo.role}</span>
                            <span style={{ fontWeight: 600, color: isHazardous ? '#f87171' : '#34d399' }}>
                              {isHazardous ? '⚠️ Scope Hazard' : '✓ Valid'}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* 1-Click Jump to CI PR Gate */}
                <div style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: '8px', padding: '12px 14px', marginTop: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                  <div>
                    <div style={{ fontWeight: 700, color: '#f0f6fc', fontSize: '0.84rem' }}>
                      Deterministic GitHub Actions CI Contract Gate
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#8b949e', marginTop: '2px' }}>
                      Inspect how Ascona automatically blocks PRs that mutate this schema without financial approval.
                    </div>
                  </div>
                  <button
                    type="button"
                    className="workbench-sm-btn active"
                    onClick={() => {
                      setActiveTab('ci-gate');
                      showToast("Opened Pull Request #342 Inspector.");
                    }}
                  >
                    Open PR Inspector →
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 2: Deterministic CI Merge Gate (GitHub Actions) */}
        {activeTab === 'ci-gate' && (
          <div className="pr-container">
            {/* PR Header Box */}
            <div className="pr-header-box">
              <div className="pr-title-row">
                <h3 className="pr-title-text">
                  <span>feat(billing): enterprise mid-cycle plan upgrades</span>
                  <span style={{ color: '#8b949e', fontWeight: 400 }}>#342</span>
                </h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <span className={`pr-status-badge ${prMerged ? 'merged' : isPrPatched ? 'passed' : 'blocked'}`}>
                    {prMerged ? '🟣 Merged' : isPrPatched ? '✔ Ready to Merge' : '❌ Merge Blocked'}
                  </span>
                  <button
                    type="button"
                    className="workbench-btn-primary"
                    onClick={handleTogglePrPatch}
                    disabled={isEvaluatingCi || prMerged}
                  >
                    {isEvaluatingCi
                      ? 'Evaluating CI...'
                      : isPrPatched
                      ? '↺ Re-trigger Contract Violation'
                      : '⚡ Apply Autonomous Contract Guard Patch'}
                  </button>
                  {prMerged && (
                    <button
                      type="button"
                      className="workbench-btn-secondary"
                      onClick={handleResetPrState}
                    >
                      ↺ Re-test PR
                    </button>
                  )}
                </div>
              </div>

              <div className="pr-meta-row">
                <span style={{ fontWeight: 700, color: '#fff' }}>cursor[bot]</span>
                <span>wants to merge 3 commits into</span>
                <code style={{ background: 'rgba(255,255,255,0.08)', padding: '2px 6px', borderRadius: '4px', color: '#f0f6fc' }}>main</code>
                <span>from</span>
                <code style={{ background: 'rgba(255,255,255,0.08)', padding: '2px 6px', borderRadius: '4px', color: '#f0f6fc' }}>cursor/agent-billing-upgrade-342</code>
                <span>· Linked to <strong style={{ color: '#38bdf8' }}>BILL-502</strong>, <strong style={{ color: '#38bdf8' }}>BILL-503</strong></span>
              </div>

              {/* Sub-tabs inside PR */}
              <div style={{ display: 'flex', gap: '8px', marginTop: '14px', borderTop: '1px solid #30363d', paddingTop: '10px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className={`workbench-sm-btn ${activePrSubTab === 'conversation' ? 'active' : ''}`}
                  onClick={() => setActivePrSubTab('conversation')}
                >
                  Conversation ({3 + customComments.length})
                </button>
                <button
                  type="button"
                  className={`workbench-sm-btn ${activePrSubTab === 'commits' ? 'active' : ''}`}
                  onClick={() => setActivePrSubTab('commits')}
                >
                  Commits (3)
                </button>
                <button
                  type="button"
                  className={`workbench-sm-btn ${activePrSubTab === 'checks' ? 'active' : ''}`}
                  onClick={() => setActivePrSubTab('checks')}
                >
                  Checks (3) {!isPrPatched && !prMerged && <span style={{ color: '#f87171' }}>• 1 failing</span>}
                </button>
                <button
                  type="button"
                  className={`workbench-sm-btn ${activePrSubTab === 'diff' ? 'active' : ''}`}
                  onClick={() => setActivePrSubTab('diff')}
                >
                  Files Changed (2)
                </button>
                <button
                  type="button"
                  className={`workbench-sm-btn ${activePrSubTab === 'gherkin' ? 'active' : ''}`}
                  onClick={() => setActivePrSubTab('gherkin')}
                >
                  Gherkin Contract Specification
                </button>
              </div>
            </div>

            {/* PR Sub-view: Conversation */}
            {activePrSubTab === 'conversation' && (
              <div className="pr-conversation-stream">
                {/* Initial PR Description */}
                <div className="pr-comment-card">
                  <div className="pr-comment-header">
                    <span className="pr-comment-avatar">🤖</span>
                    <strong style={{ color: '#fff' }}>cursor[bot]</strong>
                    <span className="pr-badge-agent">AI Coding Agent</span>
                    <span style={{ fontSize: '0.75rem', color: '#8b949e', marginLeft: 'auto' }}>28 mins ago</span>
                  </div>
                  <div className="pr-comment-body">
                    <p style={{ margin: '0 0 8px' }}>
                      Generated mid-cycle billing upgrade implementation from product brief <code>RFC-408</code>. Mutated <code>BillingUpgradeHandler.ts</code> to compute prorated charges and commit tier mutations to <code>ledger-db</code>.
                    </p>
                    <div style={{ background: '#0d1117', padding: '8px 12px', borderRadius: '4px', border: '1px solid #30363d', fontSize: '0.78rem' }}>
                      Linked Jira Stories: <strong>BILL-502</strong>, <strong>BILL-503</strong> · Target Sprint: <strong>Sprint 24</strong>
                    </div>
                  </div>
                </div>

                {/* CI Contract Gate Bot Alert */}
                <div className={`pr-comment-card ${isPrPatched ? 'passed' : 'violation'}`}>
                  <div className="pr-comment-header">
                    <span className="pr-comment-avatar">🛡️</span>
                    <strong style={{ color: '#fff' }}>ascona-ci[bot]</strong>
                    <span className="pr-badge-bot">Spec-to-PR Control Plane</span>
                    <span style={{ fontSize: '0.75rem', color: '#8b949e', marginLeft: 'auto' }}>
                      {isPrPatched ? 'Just now' : '26 mins ago'}
                    </span>
                  </div>
                  <div className="pr-comment-body">
                    {!isPrPatched ? (
                      <>
                        <h5 style={{ color: '#f87171', margin: '0 0 6px' }}>
                          ❌ Deterministic AST Contract Gate Blocked (Check Failed)
                        </h5>
                        <p style={{ margin: '0 0 8px', fontSize: '0.84rem' }}>
                          Ascona AST compiler detected a breaking contract violation in <code>BillingUpgradeHandler.ts</code>. The agent omitted the mandatory financial controller security guard.
                        </p>
                        <div className="ci-fail-box">
                          <strong>Violated Gherkin Acceptance Contract (BILL-502):</strong><br />
                          <code>Then verify caller possesses 'billing:finance_override' scope</code><br />
                          <em>Downstream Impact: Without this check, unauthenticated tier promotions will create unrecoverable GAAP billing disputes in ledger-db.</em>
                        </div>
                      </>
                    ) : (
                      <>
                        <h5 style={{ color: '#34d399', margin: '0 0 6px' }}>
                          ✔ Autonomous Contract Guard Patch Verified (Check Passed)
                        </h5>
                        <p style={{ margin: 0, fontSize: '0.84rem' }}>
                          100% AST contract compliance verified against approved Gherkin specification (BILL-502). The <code>verifyFinanceApproval()</code> guard is confirmed present across all 14 microservice dependency paths. Ready to merge.
                        </p>
                      </>
                    )}
                  </div>
                </div>

                {/* Staff Architect Review */}
                <div className="pr-comment-card">
                  <div className="pr-comment-header">
                    <span className="pr-comment-avatar">👩‍💻</span>
                    <strong style={{ color: '#fff' }}>Elena Rostova</strong>
                    <span className="pr-badge-architect">Staff Platform Architect</span>
                    <span style={{ fontSize: '0.75rem', color: '#8b949e', marginLeft: 'auto' }}>20 mins ago</span>
                  </div>
                  <div className="pr-comment-body">
                    <p style={{ margin: 0, fontSize: '0.84rem' }}>
                      {isPrPatched
                        ? "✔ Approved: The contract guard patch properly validates the financial controller token before applying ledger commits. Good to ship."
                        : "Blocking merge: Mid-cycle tier upgrades on annual contracts cannot bypass controller approval. Please click 'Apply Autonomous Contract Guard Patch' or manually inject the check before we merge into main."}
                    </p>
                  </div>
                </div>

                {/* Custom User Comments */}
                {customComments.map(c => (
                  <div key={c.id} className="pr-comment-card">
                    <div className="pr-comment-header">
                      <span className="pr-comment-avatar">{c.avatar}</span>
                      <strong style={{ color: '#fff' }}>{c.author}</strong>
                      <span className="pr-badge-agent">{c.role}</span>
                      <span style={{ fontSize: '0.75rem', color: '#8b949e', marginLeft: 'auto' }}>{c.time}</span>
                    </div>
                    <div className="pr-comment-body">
                      <p style={{ margin: 0, fontSize: '0.84rem' }}>{c.content}</p>
                    </div>
                  </div>
                ))}

                {/* Add Comment Box */}
                <form onSubmit={handleAddComment} style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: '8px', padding: '12px' }}>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#94a3b8', marginBottom: '6px' }}>
                    Leave a review comment on PR #342:
                  </label>
                  <textarea
                    rows={2}
                    value={userCommentText}
                    onChange={(e) => setUserCommentText(e.target.value)}
                    placeholder="Type an architectural review comment or prompt for the agent..."
                    style={{ width: '100%', background: '#0d1117', border: '1px solid #30363d', borderRadius: '6px', color: '#f0f6fc', padding: '8px', fontSize: '0.84rem', resize: 'vertical', boxSizing: 'border-box' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
                    <button type="submit" className="workbench-sm-btn active" disabled={!userCommentText.trim()}>
                      Comment
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* PR Sub-view: Commits */}
            {activePrSubTab === 'commits' && (
              <div style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: '8px', overflow: 'hidden' }}>
                <div style={{ padding: '10px 14px', borderBottom: '1px solid #30363d', fontSize: '0.84rem', fontWeight: 600, color: '#f0f6fc' }}>
                  Commits on branch <code>cursor/agent-billing-upgrade-342</code>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(48,54,61,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontWeight: 600, color: '#f0f6fc', fontSize: '0.84rem' }}>
                        feat(billing): add handleMidCycleUpgrade route handler
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#8b949e', marginTop: '2px' }}>
                        cursor[bot] committed 28 mins ago
                      </div>
                    </div>
                    <code style={{ background: 'rgba(255,255,255,0.08)', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem' }}>c4a1e90</code>
                  </div>

                  <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(48,54,61,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontWeight: 600, color: '#f0f6fc', fontSize: '0.84rem' }}>
                        refactor(subscription): wire proration quote calculator
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#8b949e', marginTop: '2px' }}>
                        cursor[bot] committed 25 mins ago
                      </div>
                    </div>
                    <code style={{ background: 'rgba(255,255,255,0.08)', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem' }}>b738d21</code>
                  </div>

                  <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontWeight: 600, color: isPrPatched ? '#34d399' : '#8b949e', fontSize: '0.84rem' }}>
                        {isPrPatched
                          ? "fix(ascona-guard): inject verifyFinanceApproval security guard"
                          : "build: initial commit boundary"}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#8b949e', marginTop: '2px' }}>
                        {isPrPatched ? "ascona[bot] committed just now" : "cursor[bot] committed 24 mins ago"}
                      </div>
                    </div>
                    <code style={{ background: 'rgba(255,255,255,0.08)', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem' }}>
                      {isPrPatched ? "8f21a44" : "1a09d3c"}
                    </code>
                  </div>
                </div>
              </div>
            )}

            {/* PR Sub-view: Diff */}
            {activePrSubTab === 'diff' && (
              <div className="diff-viewer">
                <div className="diff-file-header">
                  <span>repos/billing-core/src/BillingUpgradeHandler.ts</span>
                  <span style={{ fontSize: '0.75rem', color: '#8b949e' }}>+14 -2 lines</span>
                </div>

                <div className="diff-content">
                  <div className="diff-line comment">
                    <span className="diff-num">38</span>
                    <span>{"@@ -38,7 +38,18 @@ export async function handleMidCycleUpgrade(req: Request)"}</span>
                  </div>
                  <div className="diff-line">
                    <span className="diff-num">39</span>
                    <span>{"   const { tenantId, newTierId } = req.body;"}</span>
                  </div>
                  <div className="diff-line">
                    <span className="diff-num">40</span>
                    <span>{"   const quote = await subscriptionApi.calcProration(tenantId, newTierId);"}</span>
                  </div>

                  {!isPrPatched ? (
                    <>
                      <div className="diff-line del">
                        <span className="diff-num">41</span>
                        <span>{"-  // Unchecked execution: Agent omitted controller verification!"}</span>
                      </div>
                      <div className="diff-line del">
                        <span className="diff-num">42</span>
                        <span>{"-  return await billingEngine.applyUpgrade(tenantId, quote);"}</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="diff-line add">
                        <span className="diff-num">41</span>
                        <span>{"+  // [Ascona Spec Gate Enforced]: Verify financial controller token"}</span>
                      </div>
                      <div className="diff-line add">
                        <span className="diff-num">42</span>
                        <span>{"+  const authHeader = req.headers['authorization'];"}</span>
                      </div>
                      <div className="diff-line add">
                        <span className="diff-num">43</span>
                        <span>{"+  const token = await verifyOAuthScope(authHeader, 'billing:finance_override');"}</span>
                      </div>
                      <div className="diff-line add">
                        <span className="diff-num">44</span>
                        <span>{"+  if (!token.hasApproval) throw new ForbiddenError('Controller approval required');"}</span>
                      </div>
                      <div className="diff-line add">
                        <span className="diff-num">45</span>
                        <span>{"+  return await billingEngine.applyUpgrade(tenantId, quote, token.approvalId);"}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* PR Sub-view: Checks */}
            {activePrSubTab === 'checks' && (
              <div className="ci-checks-card">
                <div className="ci-checks-header">
                  <span>GitHub Actions CI Pipeline: 3 Workflow Checks</span>
                  <span style={{ fontSize: '0.78rem', color: '#8b949e' }}>Branch: main · Commit: 4a2f89d</span>
                </div>

                {/* Check 1 */}
                <div className="ci-check-row">
                  <div className="ci-check-main">
                    <span className="ci-check-icon" style={{ color: '#238636' }}>✔</span>
                    <div className="ci-check-details">
                      <h5>build &amp; lint (ubuntu-latest)</h5>
                      <p>TypeScript compiler passed in 28s</p>
                      {expandedCheckId === 'build' && (
                        <pre style={{ background: '#0d1117', padding: '8px', borderRadius: '4px', fontSize: '0.72rem', color: '#a5b4fc', marginTop: '6px' }}>
{`$ tsc --noEmit
Found 0 errors. Watching for file changes.
$ eslint src/ --ext .ts,.tsx
✔ 0 lint errors found across 48 files.`}
                        </pre>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="workbench-sm-btn"
                    onClick={() => setExpandedCheckId(expandedCheckId === 'build' ? null : 'build')}
                  >
                    {expandedCheckId === 'build' ? 'Hide Details' : 'Details'}
                  </button>
                </div>

                {/* Check 2 */}
                <div className="ci-check-row">
                  <div className="ci-check-main">
                    <span className="ci-check-icon" style={{ color: '#238636' }}>✔</span>
                    <div className="ci-check-details">
                      <h5>unit-tests (jest)</h5>
                      <p>42 tests passed in 34s</p>
                      {expandedCheckId === 'test' && (
                        <pre style={{ background: '#0d1117', padding: '8px', borderRadius: '4px', fontSize: '0.72rem', color: '#a5b4fc', marginTop: '6px' }}>
{`PASS src/__tests__/billingUpgrade.test.ts
  ✓ computes proration delta down to the second (14ms)
  ✓ rejects negative seat upgrades (8ms)
  ✓ verifies double-entry ledger idempotency (22ms)
Tests: 42 passed, 42 total. Time: 34.2s`}
                        </pre>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="workbench-sm-btn"
                    onClick={() => setExpandedCheckId(expandedCheckId === 'test' ? null : 'test')}
                  >
                    {expandedCheckId === 'test' ? 'Hide Details' : 'Details'}
                  </button>
                </div>

                {/* Check 3: THE ASCONA CI CONTRACT GATE */}
                <div className="ci-check-row">
                  <div className="ci-check-main">
                    <span className="ci-check-icon" style={{ color: isPrPatched ? '#238636' : '#da3633' }}>
                      {isEvaluatingCi ? '⏳' : isPrPatched ? '✔' : '❌'}
                    </span>
                    <div className="ci-check-details" style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <h5>ascona/spec-contract-gate</h5>
                        <span style={{ fontSize: '0.72rem', background: 'rgba(99,102,241,0.2)', color: '#a5b4fc', padding: '1px 6px', borderRadius: '4px', fontWeight: 700 }}>
                          98.2% Precision Gate
                        </span>
                      </div>

                      {!isPrPatched ? (
                        <>
                          <p>Deterministic AST Contract Check Failed in 12s (1.8% false-positive benchmark)</p>
                          <div className="ci-fail-box">
                            <strong>AST VIOLATION in BillingUpgradeHandler.ts:</strong><br />
                            Mutating enterprise billing schema without required <code>verifyFinanceApproval()</code> security guard.<br />
                            <em>Violates Compiled Gherkin Contract: BILL-502 ("Enterprise mid-cycle upgrade requires financial controller token")</em>
                          </div>
                        </>
                      ) : (
                        <p style={{ color: '#3fb950', fontWeight: 600 }}>
                          ✔ PASSED in 12s — 100% AST contract compliance against approved Gherkin specification (BILL-502). Ready to merge.
                        </p>
                      )}

                      {expandedCheckId === 'gate' && (
                        <pre style={{ background: '#0d1117', padding: '8px', borderRadius: '4px', fontSize: '0.72rem', color: isPrPatched ? '#34d399' : '#f87171', marginTop: '6px' }}>
{isPrPatched ? `[ascona] Parsing PR AST diff across 14 microservice interfaces...
[ascona] Validating against contract rules:
  - RFC-408 / BILL-502: verifyOAuthScope('billing:finance_override') -> MATCHED (AST Node 44)
  - RFC-408 / BILL-503: atomic ledger locking -> MATCHED
[ascona] Status: COMPLIANT. Gate exit code: 0.` : `[ascona] Parsing PR AST diff across 14 microservice interfaces...
[ascona] Missing AST Guard: verifyOAuthScope('billing:finance_override') NOT FOUND.
[ascona] Violates Gherkin Scenario: 'CI PR contract compliance verification'
[ascona] Status: CONTRACT_VIOLATION. Gate exit code: 1. PR MERGE BLOCKED.`}
                        </pre>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      type="button"
                      className="workbench-sm-btn"
                      onClick={() => setExpandedCheckId(expandedCheckId === 'gate' ? null : 'gate')}
                    >
                      {expandedCheckId === 'gate' ? 'Hide Details' : 'Details'}
                    </button>
                    <button
                      type="button"
                      className="workbench-sm-btn"
                      onClick={handleTogglePrPatch}
                      disabled={isEvaluatingCi}
                    >
                      {isPrPatched ? 'Re-trigger Failure' : 'Apply Patch'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* PR Sub-view: Gherkin Contract */}
            {activePrSubTab === 'gherkin' && (
              <div style={{ background: '#0d1117', border: '1px solid #30363d', borderRadius: '8px', padding: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <h4 style={{ margin: 0, fontSize: '0.92rem', color: '#38bdf8' }}>
                    Compiled Given-When-Then Acceptance Contract (BILL-502):
                  </h4>
                  <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>Tags: @compliance @asc606 @security</span>
                </div>
                <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '0.8rem', color: '#cbd5e1', whiteSpace: 'pre-wrap', background: '#161b22', padding: '12px', borderRadius: '6px' }}>
{`Feature: Enterprise Mid-Cycle Plan Upgrades
  As a SaaS enterprise controller
  I want mid-cycle plan changes gated by certified OAuth tokens
  So that revenue recognition adheres strictly to ASC 606 GAAP compliance

  Scenario: Enterprise tier upgrade with controller validation
    Given an active enterprise tenant on Annual Starter plan
    When tenant requests mid-cycle tier upgrade with financial token
    Then verify caller possesses 'billing:finance_override' scope
    And record financial_approval_id in ledger-db audit transaction
    And emit two-phase commit confirmation to subscription-api`}
                </pre>
              </div>
            )}

            {/* PR Merge Action Strip */}
            <div className="pr-merge-footer">
              <div className="pr-merge-status">
                <span style={{ fontSize: '1.4rem' }}>{prMerged ? '🟣' : isPrPatched ? '🟢' : '🔴'}</span>
                <div>
                  <div style={{ fontWeight: 700, color: '#f0f6fc', fontSize: '0.92rem' }}>
                    {prMerged
                      ? 'Pull Request #342 merged into main.'
                      : isPrPatched
                      ? 'All contract checks passed. Branch has no conflicts with base branch.'
                      : 'Merging blocked: 1 required contract check failing (ascona/spec-contract-gate).'}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#8b949e' }}>
                    {prMerged
                      ? 'Deployed to staging. Jira ticket BILL-502 marked Done.'
                      : isPrPatched
                      ? 'Autonomous contract guard verified across all 14 microservices.'
                      : 'Ascona GitHub Action blocked 1 broken schema diff before reaching production.'}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {!prMerged && isPrPatched && !showMergeConfirm && (
                  <button
                    type="button"
                    className="pr-merge-btn"
                    onClick={() => setShowMergeConfirm(true)}
                  >
                    ✔ Merge Pull Request
                  </button>
                )}

                {showMergeConfirm && !prMerged && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button
                      type="button"
                      className="pr-merge-btn"
                      onClick={handleMergePr}
                    >
                      Confirm Merge (Squash &amp; Merge)
                    </button>
                    <button
                      type="button"
                      className="workbench-sm-btn"
                      onClick={() => setShowMergeConfirm(false)}
                    >
                      Cancel
                    </button>
                  </div>
                )}

                {!isPrPatched && !prMerged && (
                  <button
                    type="button"
                    className="pr-merge-btn"
                    disabled={true}
                  >
                    Merge Blocked by Ascona
                  </button>
                )}

                {prMerged && (
                  <button
                    type="button"
                    className="workbench-sm-btn"
                    onClick={handleResetPrState}
                  >
                    ↺ Reset / Re-test PR
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* TAB 3: Bidirectional Jira & Linear Backlog View */}
        {activeTab === 'backlog' && (
          <div className="backlog-view-container">
            {/* Left Column: Issue List */}
            <div className="workbench-card">
              <div className="workbench-card-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button
                    type="button"
                    className={`workbench-sm-btn ${activeBacklogTool === 'jira' ? 'active' : ''}`}
                    onClick={() => {
                      setActiveBacklogTool('jira');
                      showToast("Switched view to Atlassian Jira Cloud (Project: BILL).");
                    }}
                  >
                    🔷 Jira Cloud (Project: BILL)
                  </button>
                  <button
                    type="button"
                    className={`workbench-sm-btn ${activeBacklogTool === 'linear' ? 'active' : ''}`}
                    onClick={() => {
                      setActiveBacklogTool('linear');
                      showToast("Switched view to Linear (Team: BILL).");
                    }}
                  >
                    🔺 Linear (Team: BILL)
                  </button>
                </div>

                <div className="workbench-card-actions">
                  <button
                    type="button"
                    className="workbench-sm-btn"
                    onClick={handleSyncBacklog}
                    disabled={isSyncingBacklog}
                  >
                    {isSyncingBacklog ? 'Syncing...' : '🔄 2-Way Sync Now'}
                  </button>
                </div>
              </div>

              {/* Status Filters */}
              <div style={{ padding: '8px 14px', borderBottom: '1px solid #30363d', display: 'flex', gap: '6px', flexWrap: 'wrap', background: '#161b22' }}>
                {['all', 'Sprint Ready', 'Ready for Dev', 'QA Ready', 'Done'].map(status => (
                  <button
                    key={status}
                    type="button"
                    className={`workbench-sm-btn ${backlogFilter === status ? 'active' : ''}`}
                    onClick={() => setBacklogFilter(status)}
                    style={{ fontSize: '0.72rem', padding: '2px 8px' }}
                  >
                    {status === 'all' ? 'All (4)' : status}
                  </button>
                ))}
              </div>

              <div style={{ padding: '14px' }}>
                <div style={{ fontSize: '0.75rem', color: '#8b949e', marginBottom: '10px' }}>
                  Last Synced: <strong style={{ color: '#34d399' }}>{lastSyncTime}</strong> · Click any issue to inspect:
                </div>

                {filteredIssues.map(issue => (
                  <div
                    key={issue.key}
                    className={`ticket-item-card ${selectedIssueKey === issue.key ? 'selected' : ''}`}
                    onClick={() => {
                      setSelectedIssueKey(issue.key);
                      showToast(`Opened issue ${issue.key}: ${issue.title}`);
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', flex: 1, overflow: 'hidden' }}>
                      <span className="ticket-key-badge">{issue.key}</span>
                      <span style={{ fontSize: '0.84rem', color: '#f0f6fc', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {issue.title}
                      </span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0, marginLeft: '12px' }}>
                      <span style={{ fontSize: '0.72rem', background: 'rgba(99,102,241,0.2)', color: '#a5b4fc', padding: '2px 6px', borderRadius: '4px', fontWeight: 700 }}>
                        {issue.points} pts
                      </span>
                      <span style={{ fontSize: '0.72rem', background: issue.status === 'Done' ? 'rgba(16,185,129,0.2)' : 'rgba(255,255,255,0.08)', color: issue.status === 'Done' ? '#34d399' : '#8b949e', padding: '2px 6px', borderRadius: '4px' }}>
                        {issue.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Column: Ticket Inspection Detail */}
            <div className="ticket-detail-panel">
              <div className="ticket-detail-header">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ fontFamily: 'monospace', fontWeight: 800, color: '#38bdf8', fontSize: '1.05rem' }}>
                    {activeIssue.key}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <select
                      value={activeIssue.status}
                      onChange={(e) => handleUpdateIssueStatus(activeIssue.key, e.target.value)}
                      style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: '4px', color: '#34d399', fontSize: '0.75rem', fontWeight: 700, padding: '3px 6px' }}
                    >
                      <option value="Sprint Ready">Sprint Ready</option>
                      <option value="Ready for Dev">Ready for Dev</option>
                      <option value="QA Ready">QA Ready</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Done">Done</option>
                    </select>
                  </div>
                </div>

                <h3 className="ticket-detail-title">{activeIssue.title}</h3>

                <div className="ticket-detail-meta">
                  <span>Type: <strong>{activeIssue.type}</strong></span>
                  <span>Service: <strong>{activeIssue.service}</strong></span>
                  <span>Assignee: <strong>{activeIssue.assignee}</strong></span>
                  <span>
                    Points:{' '}
                    <button
                      type="button"
                      onClick={() => handleAdjustPoints(activeIssue.key, -1)}
                      style={{ background: 'transparent', border: 'none', color: '#38bdf8', cursor: 'pointer', padding: '0 4px', fontWeight: 800 }}
                    >
                      -
                    </button>
                    <strong>{activeIssue.points} pts</strong>
                    <button
                      type="button"
                      onClick={() => handleAdjustPoints(activeIssue.key, 1)}
                      style={{ background: 'transparent', border: 'none', color: '#38bdf8', cursor: 'pointer', padding: '0 4px', fontWeight: 800 }}
                    >
                      +
                    </button>
                  </span>
                </div>
              </div>

              <div>
                <h5 style={{ margin: '0 0 6px', fontSize: '0.84rem', color: '#a5b4fc', textTransform: 'uppercase' }}>
                  Persona-Based User Story:
                </h5>
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#cbd5e1', lineHeight: 1.5 }}>
                  "{activeIssue.story}"
                </p>
              </div>

              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <h5 style={{ margin: 0, fontSize: '0.84rem', color: '#a5b4fc', textTransform: 'uppercase' }}>
                    Given-When-Then BDD Acceptance Criteria:
                  </h5>
                  <button
                    type="button"
                    className="workbench-sm-btn"
                    onClick={() => {
                      if (typeof navigator !== 'undefined' && navigator.clipboard) {
                        navigator.clipboard.writeText(activeIssue.gherkin);
                        showToast(`Copied ${activeIssue.key} Gherkin criteria to clipboard.`);
                      }
                    }}
                    style={{ fontSize: '0.7rem', padding: '2px 6px' }}
                  >
                    Copy Gherkin
                  </button>
                </div>
                <pre style={{ margin: 0, background: '#161b22', border: '1px solid #30363d', borderRadius: '6px', padding: '12px', fontFamily: 'monospace', fontSize: '0.78rem', color: '#e2e8f0', whiteSpace: 'pre-wrap' }}>
                  {activeIssue.gherkin}
                </pre>
              </div>

              <div style={{ marginTop: 'auto', display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  className="workbench-btn-primary"
                  onClick={handleSyncBacklog}
                  disabled={isSyncingBacklog}
                  style={{ flex: 1, justifyContent: 'center' }}
                >
                  {isSyncingBacklog ? 'Syncing...' : '⚡ Push Updates to Jira REST v3'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* TAB 4: Production Benchmarks & ROI */}
        {activeTab === 'telemetry' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div className="telemetry-kpi-grid">
              <div className="telemetry-kpi-card">
                <div className="telemetry-kpi-val green">-64.2%</div>
                <div className="telemetry-kpi-lbl">Planning Overhead Time Cut (120 min → 43 min)</div>
              </div>
              <div className="telemetry-kpi-card">
                <div className="telemetry-kpi-val green">46% → 81%</div>
                <div className="telemetry-kpi-lbl">First-Pass Agent PR Merge Rate (+35% lift)</div>
              </div>
              <div className="telemetry-kpi-card">
                <div className="telemetry-kpi-val">98.2%</div>
                <div className="telemetry-kpi-lbl">CI Contract Gate Precision (1.8% false positive rate)</div>
              </div>
              <div className="telemetry-kpi-card">
                <div className="telemetry-kpi-val green">&gt;${monthlyReworkSavings.toLocaleString()} / mo</div>
                <div className="telemetry-kpi-lbl">Prevented Rework Savings for {teamSizeInput}-Engineer Org</div>
              </div>
            </div>

            {/* Interactive ROI Calculator for Enterprise Prospect */}
            <div style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: '8px', padding: '20px' }}>
              <h3 style={{ margin: '0 0 6px', color: '#fff', fontSize: '1.05rem' }}>
                ⚡ Custom ROI &amp; Prevented Rework Calculator
              </h3>
              <p style={{ margin: '0 0 16px', color: '#94a3b8', fontSize: '0.84rem' }}>
                Calibrate Ascona's financial impact for your specific engineering organization size and compensation baseline:
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: '#cbd5e1', marginBottom: '4px' }}>
                    Engineering Team Size: <strong style={{ color: '#38bdf8' }}>{teamSizeInput} engineers</strong>
                  </label>
                  <input
                    type="range"
                    min={10}
                    max={250}
                    step={5}
                    value={teamSizeInput}
                    onChange={(e) => setTeamSizeInput(parseInt(e.target.value, 10))}
                    style={{ width: '100%', cursor: 'pointer' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: '#cbd5e1', marginBottom: '4px' }}>
                    Avg Engineer Salary: <strong style={{ color: '#38bdf8' }}>${avgSalaryInput.toLocaleString()} / yr</strong>
                  </label>
                  <input
                    type="range"
                    min={120000}
                    max={250000}
                    step={5000}
                    value={avgSalaryInput}
                    onChange={(e) => setAvgSalaryInput(parseInt(e.target.value, 10))}
                    style={{ width: '100%', cursor: 'pointer' }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', background: '#0d1117', padding: '14px', borderRadius: '6px', border: '1px solid #30363d' }}>
                <div>
                  <div style={{ fontSize: '0.72rem', color: '#8b949e', textTransform: 'uppercase' }}>Hours Saved per Year</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#34d399' }}>{hoursSavedPerYear.toLocaleString()} hrs</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.72rem', color: '#8b949e', textTransform: 'uppercase' }}>Annual Dollar Savings</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#34d399' }}>${annualSavingsDollars.toLocaleString()}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.72rem', color: '#8b949e', textTransform: 'uppercase' }}>Payback Period</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#38bdf8' }}>&lt; 14 Days</div>
                </div>
              </div>
            </div>

            <div style={{ background: '#0d1117', border: '1px solid #30363d', borderRadius: '8px', padding: '20px' }}>
              <h3 style={{ margin: '0 0 8px', color: '#fff', fontSize: '1.1rem' }}>
                Audited 6-Spec Benchmark · 5 B2B SaaS Engineering Teams · 30 RFCs / 210 Stories
              </h3>
              <p style={{ margin: '0 0 16px', color: '#94a3b8', fontSize: '0.88rem' }}>
                Evaluated against real production sprint telemetry across mid-market B2B software companies. As coding agents accelerate local editor synthesis, Ascona serves as the upstream and CI control plane to protect architectural boundaries.
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px' }}>
                <div style={{ background: '#161b22', padding: '14px', borderRadius: '6px', border: '1px solid #30363d' }}>
                  <h5 style={{ margin: '0 0 4px', color: '#38bdf8' }}>14,800+ Indexed Diff Pairs</h5>
                  <p style={{ margin: 0, fontSize: '0.8rem', color: '#cbd5e1' }}>
                    Historical commit diff and revert datasets train Ascona's cross-tenant evaluator to detect breaking schema drift before code is committed.
                  </p>
                </div>
                <div style={{ background: '#161b22', padding: '14px', borderRadius: '6px', border: '1px solid #30363d' }}>
                  <h5 style={{ margin: '0 0 4px', color: '#38bdf8' }}>Zero AST Hallucinations</h5>
                  <p style={{ margin: 0, fontSize: '0.8rem', color: '#cbd5e1' }}>
                    Deterministic Gherkin compilation eliminates prompt guessing by binding directly to real repo AST interfaces.
                  </p>
                </div>
                <div style={{ background: '#161b22', padding: '14px', borderRadius: '6px', border: '1px solid #30363d' }}>
                  <h5 style={{ margin: '0 0 4px', color: '#38bdf8' }}>Atlassian Marketplace Native</h5>
                  <p style={{ margin: 0, fontSize: '0.8rem', color: '#cbd5e1' }}>
                    Zero tool migration friction: sits directly on your existing Jira licenses co-termed via Atlassian Marketplace (App ID: 3398389661).
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
